/**
 * PWA install card — Android install button (with debug info when the
 * prompt is unavailable) and iOS manual add-to-home-screen instructions.
 */

import React from 'react';
import type { PWADebugInfo } from '../../../hooks/usePWAInstall';
import type { BrowserInfo } from '../utils/browserInfo';

interface InstallAppSectionProps {
  browserInfo: BrowserInfo;
  isAndroid: boolean;
  canInstall: boolean;
  isPrompting: boolean;
  onInstall: () => void;
  debugInfo: PWADebugInfo;
}

export const InstallAppSection: React.FC<InstallAppSectionProps> = ({
  browserInfo,
  isAndroid,
  canInstall,
  isPrompting,
  onInstall,
  debugInfo,
}) => {
  return (
    <div className="bg-surface-muted rounded-lg p-4 border border-line">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold bg-hue-purple-soft text-hue-purple-deep">
          <i className="fa-solid fa-mobile-screen"></i>
        </div>
        <div>
          <span className="font-semibold text-ink">Install App</span>
          <p className="text-xs text-ink-muted">
            Add BOTR Tools to your home screen for quick access
          </p>
        </div>
      </div>

      {/* Android - Always show install button, disabled if prompt not available */}
      {isAndroid && (
        <>
          <button
            onClick={onInstall}
            disabled={!canInstall || isPrompting}
            className={`w-full px-4 py-2.5 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-2 transition-colors flex items-center justify-center gap-2 ${
              canInstall
                ? 'bg-purple-600 text-ink-inverse hover:bg-purple-700'
                : 'bg-surface-deep text-ink-muted cursor-not-allowed'
            }`}
          >
            {isPrompting ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin"></i>
                <span>Installing...</span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-download"></i>
                <span>Install BOTR Tools</span>
              </>
            )}
          </button>
          {/* Debug info when install not available */}
          {!canInstall && (
            <div className="mt-3 p-2 bg-surface-inset rounded text-xs font-mono text-ink-body space-y-1">
              <p className="font-semibold text-ink-body">PWA Debug Info:</p>
              <p>
                <span className="text-ink-muted">HTTPS:</span>{' '}
                <span className={debugInfo.isSecureContext ? 'text-success' : 'text-danger'}>
                  {debugInfo.isSecureContext ? 'Yes' : 'No'}
                </span>
              </p>
              <p>
                <span className="text-ink-muted">Service Worker:</span>{' '}
                <span className={debugInfo.serviceWorkerStatus === 'registered' ? 'text-success' : 'text-danger'}>
                  {debugInfo.serviceWorkerStatus}
                </span>
                {debugInfo.serviceWorkerState && (
                  <span className="text-ink-faint"> ({debugInfo.serviceWorkerState})</span>
                )}
                {debugInfo.serviceWorkerScope && (
                  <span className="text-ink-faint block ml-2 truncate">
                    Scope: {debugInfo.serviceWorkerScope}
                  </span>
                )}
              </p>
              <p>
                <span className="text-ink-muted">Manifest:</span>{' '}
                <span className={debugInfo.manifestStatus === 'loaded' ? 'text-success' : 'text-danger'}>
                  {debugInfo.manifestStatus}
                </span>
                {debugInfo.manifestError && (
                  <span className="text-danger-mid block ml-2">
                    {debugInfo.manifestError}
                  </span>
                )}
              </p>
              <p>
                <span className="text-ink-muted">Icons:</span>{' '}
                <span className={debugInfo.iconsAccessible === true ? 'text-success' : debugInfo.iconsAccessible === false ? 'text-danger' : 'text-ink-faint'}>
                  {debugInfo.iconsAccessible === true ? 'Accessible' : debugInfo.iconsAccessible === false ? 'Not accessible' : 'Checking...'}
                </span>
              </p>
              <p>
                <span className="text-ink-muted">beforeinstallprompt:</span>{' '}
                <span className={debugInfo.beforeInstallPromptFired ? 'text-success' : 'text-warning'}>
                  {debugInfo.beforeInstallPromptFired ? 'Fired' : 'Not fired yet'}
                </span>
              </p>
              <p className="pt-1 border-t border-line mt-1">
                <span className="text-ink-muted">UA:</span>{' '}
                <span className="text-ink-faint break-all text-[10px]">
                  {debugInfo.userAgent}
                </span>
              </p>
            </div>
          )}
        </>
      )}

      {/* iOS - Show manual instructions */}
      {browserInfo.isIOS && (
        <div className="bg-surface/60 rounded-md p-3 space-y-2">
          <p className="font-medium text-xs text-ink-body">How to install on iOS:</p>
          <ol className="text-xs space-y-1.5 ml-4 list-decimal text-ink-body">
            <li>
              Tap the <strong>Share</strong> button{' '}
              <span className="inline-flex items-center justify-center w-5 h-5 bg-hue-blue-soft rounded text-hue-blue-deep text-[10px]">
                <i className="fa-solid fa-arrow-up-from-bracket"></i>
              </span>{' '}
              at the bottom of Safari
            </li>
            <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
            <li>Tap <strong>"Add"</strong> in the top right corner</li>
          </ol>
        </div>
      )}
    </div>
  );
};
